import { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { TIMEFRAME_OPTIONS, PROFIT_CURRENCIES } from '@/mocks/profit';
import { formatVN, Label, NumberInput, SelectInput, QuickChips, Toggle, Switch, Stat } from '../ui';
import { NextStep } from './ProfitTab';

const COMPOUND_ACCENT = '#f59e0b';
const REINVEST_COLOR = '#f59e0b';
const NO_REINVEST_COLOR = '#94a3b8';

const tooltipStyle = {
  borderRadius: 12,
  border: '1px solid #fef3c7',
  fontSize: 12,
  boxShadow: '0 12px 30px -12px rgba(245,158,11,0.25)',
};

const MILESTONE_LABELS = ['3 tháng', '6 tháng', '1 năm', '3 năm', '5 năm'];

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

function computeSeries(P: number, r: number, n: number, C: number, reinvest: boolean) {
  const data: { period: number; value: number }[] = [];
  data.push({ period: 0, value: round2(P) });
  let value = P;
  for (let i = 1; i <= n; i++) {
    if (reinvest) {
      value = value * (1 + r) + C;
    } else {
      value = P + P * r * i + C * i;
    }
    data.push({ period: i, value: round2(value) });
  }
  return data;
}

function valueAt(P: number, r: number, n: number, C: number, reinvest: boolean): number {
  if (reinvest) {
    let v = P;
    for (let i = 0; i < n; i++) v = v * (1 + r) + C;
    return v;
  }
  return P + P * r * n + C * n;
}

export default function CompoundTab({ onNext }: { onNext: () => void }) {
  const [capital, setCapital] = useState(1000);
  const [rate, setRate] = useState(5);
  const [period, setPeriod] = useState('month');
  const [timeframe, setTimeframe] = useState('1Y');
  const [contribution, setContribution] = useState(100);
  const [reinvest, setReinvest] = useState(true);
  const [chartRange, setChartRange] = useState('5Y');

  const [goalCapital, setGoalCapital] = useState(1000);
  const [goalTarget, setGoalTarget] = useState(5000);
  const [goalDuration, setGoalDuration] = useState(36);
  const [currency, setCurrency] = useState('USD');

  const tf = TIMEFRAME_OPTIONS.find((t) => t.value === timeframe) ?? TIMEFRAME_OPTIONS[2];
  const n = period === 'month' ? tf.months : tf.weeks;
  const r = rate / 100;
  const curr = useMemo(() => PROFIT_CURRENCIES.find((c) => c.value === currency) ?? PROFIT_CURRENCIES[0], [currency]);
  const fxRate = curr.rate;

  // Chart series: driven by the selected view range (1Y / 3Y / 5Y) and every current input
  const chartPeriods = chartRange === '1Y' ? (period === 'month' ? 12 : 52) : chartRange === '3Y' ? (period === 'month' ? 36 : 156) : (period === 'month' ? 60 : 260);
  const chartData = useMemo(
    () => computeSeries(capital, r, chartPeriods, contribution, reinvest).map((p) => ({ period: p.period, value: p.value * fxRate })),
    [capital, r, chartPeriods, contribution, reinvest, fxRate],
  );

  const series = useMemo(() => computeSeries(capital, r, n, contribution, reinvest), [capital, r, n, contribution, reinvest]);
  const finalValue = series[series.length - 1].value;
  const totalContribution = contribution * n;
  const cumulativeProfit = finalValue - capital - totalContribution;

  // Milestones
  const milestonePeriods = period === 'month' ? [3, 6, 12, 36, 60] : [13, 26, 52, 156, 260];
  const milestones = milestonePeriods.map((mp, i) => ({
    label: MILESTONE_LABELS[i],
    value: valueAt(capital, r, mp, contribution, reinvest),
  }));

  // Reinvest vs not comparison
  const compareRange = period === 'month' ? 60 : 260;
  const withReinvest = useMemo(() => computeSeries(capital, r, compareRange, contribution, true), [capital, r, compareRange, contribution]);
  const withoutReinvest = useMemo(() => computeSeries(capital, r, compareRange, contribution, false), [capital, r, compareRange, contribution]);
  const compareData = withReinvest.map((p, i) => ({
    period: p.period,
    reinvest: p.value * fxRate,
    noReinvest: withoutReinvest[i].value * fxRate,
  }));

  // Goal mode
  const requiredRate = goalDuration > 0 && goalCapital > 0 && goalTarget > goalCapital
    ? (Math.pow(goalTarget / goalCapital, 1 / goalDuration) - 1) * 100
    : 0;

  const fmt = (v: number) => formatVN(v, 0, 0);
  const money = (v: number) => fmt(v * fxRate);

  return (
    <div className="space-y-6">
      {/* Disclaimer */}
      <div className="flex items-start gap-3 rounded-2xl border border-amber-100 bg-amber-50/60 p-4">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
          <i className="ri-information-line text-lg" />
        </span>
        <p className="text-sm leading-relaxed text-slate-600">
          Đây là mô phỏng toán học dựa trên các giả định, không phải dự báo hoặc cam kết về lợi nhuận thực tế.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Left: inputs */}
        <div className="lg:col-span-3">
          <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
            <div className="mb-6 flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                <i className="ri-repeat-line text-lg" />
              </span>
              <h3 className="font-display text-lg font-bold text-slate-900">Thông số mô phỏng</h3>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <Label>Vốn ban đầu</Label>
                  <NumberInput value={capital} onChange={setCapital} suffix="USD" min={0} step={100} />
                </div>
                <div>
                  <Label>Tiền tệ tài khoản</Label>
                  <SelectInput value={currency} onChange={setCurrency} options={PROFIT_CURRENCIES.map((c) => ({ value: c.value, label: c.label }))} />
                </div>
              </div>

              <div>
                <Label hint={`${formatVN(rate, 2, 2)}%`}>Tỷ suất lợi nhuận mỗi kỳ</Label>
                <NumberInput value={rate} onChange={setRate} suffix="%" min={0} step={0.5} />
                <div className="mt-2.5">
                  <Toggle value={period} onChange={setPeriod} options={[{ value: 'week', label: 'Theo tuần' }, { value: 'month', label: 'Theo tháng' }]} />
                </div>
              </div>

              <div>
                <Label>Thời gian</Label>
                <div className="flex flex-wrap gap-2">
                  {TIMEFRAME_OPTIONS.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setTimeframe(t.value)}
                      className={`whitespace-nowrap rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors ${
                        timeframe === t.value ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Khoản nạp thêm</Label>
                <NumberInput value={contribution} onChange={setContribution} suffix="USD / kỳ" min={0} step={10} />
              </div>

              <div className="flex items-center justify-between rounded-xl border border-slate-200/80 bg-slate-50/60 p-4">
                <div>
                  <p className="text-sm font-semibold text-slate-700">Tái đầu tư</p>
                  <p className="mt-0.5 text-xs text-slate-400">{reinvest ? 'Lợi nhuận được cộng vào vốn' : 'Lợi nhuận không cộng vào vốn'}</p>
                </div>
                <Switch checked={reinvest} onChange={setReinvest} />
              </div>
            </div>
          </div>
        </div>

        {/* Right: result */}
        <div className="lg:col-span-2">
          <div className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-amber-100 bg-gradient-to-br from-amber-50/70 to-white p-6 md:p-7">
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-amber-200/40 blur-3xl" />
            <div className="relative">
              <p className="text-sm font-semibold text-slate-500">Vốn cuối kỳ</p>
              <div className="mt-2 flex items-end gap-2">
                <span className="font-mono text-5xl font-bold leading-none tabular-nums text-slate-900 md:text-6xl">{money(finalValue)}</span>
                <span className="mb-1 font-display text-xl font-bold text-slate-400">{curr.value}</span>
              </div>
              <p className="mt-2 text-xs text-slate-500">Sau {formatVN(n, 0, 0)} kỳ ({tf.label})</p>

              <div className="mt-6 grid grid-cols-1 gap-2.5">
                <Stat label="Vốn ban đầu" value={`${money(capital)} ${curr.value}`} />
                <Stat label="Tổng tiền nạp thêm" value={`${money(totalContribution)} ${curr.value}`} />
                <Stat label="Lợi nhuận tích lũy" value={`${money(cumulativeProfit)} ${curr.value}`} accent={COMPOUND_ACCENT} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Growth curve */}
      <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="font-display text-lg font-bold text-slate-900">Vốn thay đổi theo thời gian</h3>
          <div className="flex gap-1 rounded-xl bg-slate-100 p-1">
            {['1Y', '3Y', '5Y'].map((range) => (
              <button
                key={range}
                type="button"
                onClick={() => setChartRange(range)}
                className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  chartRange === range ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {range === '1Y' ? '1 năm' : range === '3Y' ? '3 năm' : '5 năm'}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-5 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: -6, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => fmt(v)} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [`${fmt(v)} ${curr.value}`, 'Vốn']} />
              <Line type="monotone" dataKey="value" name="Vốn" stroke={COMPOUND_ACCENT} strokeWidth={3} dot={false} activeDot={{ r: 5, fill: COMPOUND_ACCENT, stroke: '#fff', strokeWidth: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Milestones */}
      <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
        <h3 className="font-display text-lg font-bold text-slate-900">Các mốc tăng trưởng</h3>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {milestones.map((m) => (
            <div key={m.label} className="rounded-2xl border border-slate-200/80 bg-slate-50/60 px-4 py-5 text-center">
              <p className="text-xs font-medium text-slate-400">{m.label}</p>
              <p className="mt-2 font-mono text-lg font-bold tabular-nums leading-none text-slate-900">{money(m.value)}</p>
              <p className="mt-1.5 text-[11px] font-medium text-slate-400">{curr.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Reinvest vs not */}
      <section>
        <h3 className="font-display text-lg font-bold text-slate-900">Tái đầu tư tạo ra sự khác biệt như thế nào?</h3>
        <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200/80 bg-white px-6">
            <div className="flex items-start gap-4 py-4">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                <i className="ri-subtract-line text-base" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">Không tái đầu tư</p>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">Lợi nhuận không được cộng vào vốn cho kỳ tiếp theo.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 py-4">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                <i className="ri-repeat-line text-base" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">Tái đầu tư</p>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">Lợi nhuận được cộng vào vốn và tham gia vào phép tính của kỳ tiếp theo.</p>
              </div>
            </div>
            <p className="py-4 text-[11px] leading-relaxed text-slate-400">Đây là mô hình toán học, không phải dự báo lợi nhuận giao dịch thực tế.</p>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={compareData} margin={{ top: 10, right: 10, left: -6, bottom: 0 }}>
                <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => fmt(v)} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number, name: string) => [`${fmt(v)} ${curr.value}`, name === 'reinvest' ? 'Tái đầu tư' : 'Không tái đầu tư']} />
                <Line type="monotone" dataKey="reinvest" name="reinvest" stroke={REINVEST_COLOR} strokeWidth={reinvest ? 3 : 2} strokeOpacity={reinvest ? 1 : 0.25} dot={false} activeDot={{ r: 5, fill: REINVEST_COLOR, stroke: '#fff', strokeWidth: 2 }} />
                <Line type="monotone" dataKey="noReinvest" name="noReinvest" stroke={NO_REINVEST_COLOR} strokeWidth={reinvest ? 2 : 3} strokeDasharray={reinvest ? '5 5' : '0'} strokeOpacity={reinvest ? 0.45 : 1} dot={false} activeDot={{ r: 5, fill: NO_REINVEST_COLOR, stroke: '#fff', strokeWidth: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Goal capital mode */}
      <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
            <i className="ri-flag-line text-lg" />
          </span>
          <h3 className="font-display text-lg font-bold text-slate-900">Mục tiêu vốn</h3>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
          <div>
            <Label>Vốn hiện tại</Label>
            <NumberInput value={goalCapital} onChange={setGoalCapital} suffix="USD" min={0} step={100} />
          </div>
          <div>
            <Label>Mục tiêu</Label>
            <NumberInput value={goalTarget} onChange={setGoalTarget} suffix="USD" min={0} step={100} />
          </div>
          <div>
            <Label hint="tháng">Thời gian</Label>
            <NumberInput value={goalDuration} onChange={setGoalDuration} suffix="tháng" min={1} step={1} />
          </div>
        </div>
        <div className="mt-4">
          <QuickChips options={[12, 36, 60]} value={goalDuration} onChange={setGoalDuration} format={(v) => (v === 12 ? '1 năm' : v === 36 ? '3 năm' : '5 năm')} />
        </div>
        <div className="mt-5 rounded-2xl border border-amber-100 bg-amber-50/50 p-5">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">Tỷ suất cần thiết mỗi tháng</p>
            <p className="font-mono text-2xl font-bold tabular-nums text-slate-900">{formatVN(requiredRate, 2, 2)}%</p>
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
            Kết quả phụ thuộc vào các giả định được nhập và không phản ánh lợi nhuận giao dịch thực tế.
          </p>
        </div>
      </div>

      <NextStep
        title="Bạn đã hoàn thành mô phỏng tăng trưởng vốn."
        desc="Bạn có thể quay lại tính toán lợi nhuận của một giao dịch."
        cta="Quay lại tính lợi nhuận"
        onClick={onNext}
      />
    </div>
  );
}